import React, { useState } from "react";
import {
    Card,
    Typography,
    Grid,
    Chip,
    makeStyles,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Divider,
    IconButton,
    Link
} from "@material-ui/core";
import {
    ExpandMore,
    Schedule,
    LocationOn,
    ConfirmationNumber,
    Person,
    Business,
    Language,
    Public,
    Description,
    AttachFile,
    Repeat,
    CloudDownload
} from "@material-ui/icons";
import moment from 'moment-timezone'

const useStyles = makeStyles((theme) => ({
    accordion: {
        borderRadius: "12px !important",
        marginBottom: theme.spacing(2),
        boxShadow: "0 4px 12px rgba(0,0,0,0.05) !important",
        border: "1px solid #eee",
        "&:before": {
            display: "none",
        },
        overflow: 'hidden'
    },
    expanded: {
        margin: "12px 0 !important",
    },
    summaryContent: {
        alignItems: "center",
        padding: "8px 0",
    },
    // Left Branding Bar
    statusIndicator: (props) => ({
        width: 6,
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        backgroundColor: props.statusColor,
    }),
    timeBox: {
        textAlign: "center",
        paddingRight: theme.spacing(2),
        borderRight: "1px solid #f0f0f0",
        minWidth: 100,
    },
    mainInfo: {
        paddingLeft: theme.spacing(2),
    },
    brandText: {
        color: "#d70001",
        fontWeight: 700,
        fontSize: "1rem",
    },
    detailsLabel: {
        fontSize: "0.75rem",
        color: "#888",
        fontWeight: 600,
        textTransform: "uppercase",
        display: "flex",
        alignItems: "center",
        marginBottom: 4,
    },
    detailsValue: {
        fontSize: "0.85rem",
        fontWeight: 500,
        color: "#333",
    },
    iconSmall: {
        fontSize: "1rem",
        marginRight: theme.spacing(0.5),
    },
    link: {
        color: "#d70001",
        fontWeight: 600,
        fontSize: "0.85rem",
        textDecoration: "none",
        "&:hover": {
            textDecoration: "underline",
        }
    }
}));

const getStatusConfig = (status) => {
    switch (status) {
        case "UNSPECIFIED":
        case "CONFIRMED":
            return { color: "#9c27b0", label: "CONFIRMED" };
        case "COMPLETED":
            return { color: "#00acc1", label: status };
        case "CHECKED_IN":
        case "STARTED":
            return { color: "#4caf50", label: status.replace("_", " ") };
        case "CANCELED":
            return { color: "#f44336", label: status.replace("_", " ") };
        case "NO_SHOW":
            return { color: "#ff9800", label: status.replace("_", " ") };
        default:
            return { color: "#9e9e9e", label: status || "PENDING" };
    }
};

const formatDate = (sec, timezoneId) => {
    if (!sec) return "--";
    return moment.unix(sec).tz(timezoneId || moment.tz.guess()).format("MMM D, YYYY");
};

const formatTime = (sec, timezoneId) => {
    if (!sec) return "--";
    return moment.unix(sec).tz(timezoneId || moment.tz.guess()).format("hh:mm A");
};

export default function CustomerBookingCard({ booking }) {
    const statusCfg = getStatusConfig(booking.status);
    const classes = useStyles({ statusColor: statusCfg.color });

    /*
    booking.summary = {
        "id": "69eee9249b70b037b1de2eae",
        "eventId": "e53b53444fb8ff49bb607345667cc282",
        "note": "Test eumary note for testing",
        "attachments": [
            {
                "fileUrl": "https://s3.ap-south-1.amazonaws.com/quezone-in-test/summary-attachment-16075a22e35d610ce1cf12edc847e764.jpg",
                "originName": "Edwin-Baptism.jpg",
                "keyName": "summary-attachment-16075a22e35d610ce1cf12edc847e764.jpg"
            }
        ],
        "followUp": {
            "remindOn": 1777833000,
            "reminderSent": false
        }
    }
    booking.comment = "Test Note .. This user is test user"

    */

    return (
        <Accordion className={classes.accordion} classes={{ expanded: classes.expanded }}>
            <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="booking-content"
                id="booking-header"
            >
                <div className={classes.statusIndicator} />

                <Grid container className={classes.summaryContent}>
                    {/* Time & Date Column */}
                    <Grid item xs={12} sm={3} md={2}>
                        <Box className={classes.timeBox}>
                            <Typography variant="h6" style={{ lineHeight: 1, fontWeight: 800 }}>
                                {formatTime(booking.startSec, booking.timezoneId)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {formatDate(booking.startSec, booking.timezoneId)}
                            </Typography>
                            <Box mt={1}>
                                <Chip
                                    label={statusCfg.label}
                                    size="small"
                                    style={{
                                        backgroundColor: statusCfg.color + "15",
                                        color: statusCfg.color,
                                        fontWeight: 700,
                                        fontSize: 10
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>

                    {/* Main Details Column */}
                    <Grid item xs={12} sm={6} md={7} className={classes.mainInfo}>
                        <Typography className={classes.brandText}>
                            {booking.serviceName}
                        </Typography>
                        <Box display="flex" alignItems="center" color="textSecondary">
                            <Person className={classes.iconSmall} />
                            <Typography variant="body2">{booking.providerName}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" color="textSecondary">
                            <LocationOn className={classes.iconSmall} />
                            <Typography variant="body2" noWrap>{booking.fullAddress}</Typography>
                        </Box>
                    </Grid>

                    {/* Quick Meta (Token/Code) Column */}
                    <Grid item xs={12} sm={3} md={3} style={{ textAlign: "right", paddingRight: 16 }}>
                        <Typography variant="caption" style={{ fontWeight: 700, color: "#888" }}>
                            TOKEN: <span style={{ color: "#333", fontSize: "1.1rem" }}>{booking.token || "N/A"}</span>
                        </Typography>
                        <Typography variant="body2" style={{ fontWeight: 600, color: "#666" }}>
                            #{booking.bookingCode}
                        </Typography>
                    </Grid>
                </Grid>
            </AccordionSummary>

            <Divider />

            <AccordionDetails>
                <Grid container spacing={3} style={{ paddingTop: 16 }}>
                    {/* Organization & Provider */}
                    <Grid item xs={12} sm={4}>
                        <Typography className={classes.detailsLabel}>
                            <Business className={classes.iconSmall} /> Organization
                        </Typography>
                        <Typography className={classes.detailsValue}>
                            {booking.organizationName}
                        </Typography>
                        <Box mt={2}>
                            <Typography className={classes.detailsLabel}>Provider Contact</Typography>
                            <Typography className={classes.detailsValue}>{booking.providerEmail}</Typography>
                        </Box>
                    </Grid>

                    {/* Logistics */}
                    <Grid item xs={12} sm={4}>
                        <Typography className={classes.detailsLabel}>
                            <Schedule className={classes.iconSmall} /> Appointment Details
                        </Typography>
                        <Typography className={classes.detailsValue}>
                            Duration: {booking.duration} Minutes
                        </Typography>
                        <Typography className={classes.detailsValue}>
                            Mode: {booking.mode} ({booking.type})
                        </Typography>
                        <Box mt={1}>
                            <Chip label={`TZ: ${booking.timezoneId}`} size="small" variant="outlined" icon={<Public />} />
                        </Box>
                    </Grid>

                    {/* Actions & Links */}
                    <Grid item xs={12} sm={4}>
                        <Typography className={classes.detailsLabel}>
                            <Language className={classes.iconSmall} /> Links & Actions
                        </Typography>
                        {booking.eventUrl && (
                            <Link href={booking.eventUrl} target="_blank" className={classes.link}>
                                Open Event Page →
                            </Link>
                        )}
                        <Box mt={1}>
                            <Typography className={classes.detailsLabel}>Coordinates</Typography>
                            <Typography variant="caption" color="textSecondary">
                                Lat: {booking.coordinates?.latitude}, Lon: {booking.coordinates?.longitude}
                            </Typography>
                        </Box>
                    </Grid>
                    {/* Booking Internal comments */}
                    {booking?.comment && (
                        <>
                            {/* Section Divider */}
                            <Grid item xs={12}>
                                <Divider />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <Typography className={classes.detailsLabel}>
                                    <Description className={classes.iconSmall} /> Internal Notes
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {booking?.comment}
                                </Typography>
                            </Grid>
                        </>
                    )}
                    {/* Booking Summary Details */}
                    {booking?.summary && (
                        <>
                            {/* Section Divider */}
                            <Grid item xs={12}>
                                <Divider />
                            </Grid>

                            {/* Notes */}
                            {booking.summary.note && (
                                <Grid item xs={12} sm={4}>
                                    <Typography className={classes.detailsLabel}>
                                        <Description className={classes.iconSmall} /> Summary Notes
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {booking.summary.note}
                                    </Typography>
                                </Grid>
                            )}

                            {/* Attachments */}
                            {booking.summary.attachments?.length > 0 && (
                                <Grid item xs={12} sm={4}>
                                    <Typography className={classes.detailsLabel}>
                                        <AttachFile className={classes.iconSmall} /> Summary Attachments
                                    </Typography>

                                    <Box display="flex" flexDirection="column" gridGap={8} mt={1}>
                                        {booking.summary.attachments.map((file, index) => (
                                            <Box
                                                key={index}
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                border="1px solid #e0e0e0"
                                                borderRadius={4}
                                                px={1}
                                                py={0.5}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="textSecondary"
                                                    style={{ maxWidth: '70%' }}
                                                    noWrap
                                                >
                                                    {file.originName ?? file.keyName}
                                                </Typography>

                                                <IconButton
                                                    component={Link}
                                                    href={file.fileUrl}
                                                    target="_blank"
                                                    size="small"
                                                >
                                                    <CloudDownload fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                </Grid>
                            )}

                            {/* Follow-up */}
                            {booking.summary.followUp?.remindOn && (
                                <Grid item xs={12} sm={4}>
                                    <Typography className={classes.detailsLabel}>
                                        <Repeat className={classes.iconSmall} /> Next Follow-up
                                    </Typography>

                                    <Box mt={1}>
                                        <Chip
                                            label={formatDate(
                                                booking.summary.followUp.remindOn,
                                                booking.timezoneId
                                            )}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Box>
                                </Grid>
                            )}
                        </>
                    )}

                    {/* Internal Technical IDs */}
                    <Grid item xs={12}>
                        <Box bgcolor="#f9f9f9" p={1} borderRadius={4} display="flex" justifyContent="space-between" flexWrap="wrap">
                            <Typography variant="caption" color="textSecondary">
                                Booking ID: {booking.id}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Service ID: {booking.serviceId}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </AccordionDetails >
        </Accordion >
    );
}